import { useMutation } from "@tanstack/react-query";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";

type CreateThemeThumbnailUploadInput = {
  themeId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
};

type CreateThemeThumbnailUploadResponse = {
  uploadUrl: string;
  objectKey: string;
  imageId: string;
  expiresAt: string;
  /** Final media-zone URL — written straight into the version's thumbnailUrl. */
  publicUrl: string;
};

/**
 * Request a presigned PUT URL for a theme gallery thumbnail (public bucket).
 * The caller PUTs the file to `uploadUrl`, then persists `publicUrl` via the
 * version-meta mutation. Mirrors the subscription-payment-proof upload flow.
 */
export function useCreateThemeThumbnailUploadMutation() {
  const mutation = useMutation({
    mutationFn: (input: CreateThemeThumbnailUploadInput) =>
      apiJsonClient<CreateThemeThumbnailUploadResponse>(
        endpoints.admin.themeThumbnailUploads,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        },
      ),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
